import { Box, Typography } from "@mui/material";
import { grey, green, red } from "@mui/material/colors";
import { useContext, useState, useEffect } from "react";
import { HiArrowNarrowRight } from "react-icons/hi";

import MoreInfoDisplay from "./MoreInfoDisplay";
import AnswerStats from "./multiplayer/AnswerStats";
import Comments from "./multiplayer/Comments";
import SeeQuestionAgainButton from "./SeeQuestionAgainBubble";
import SocialShare from "./SocialShare";
import { AlignCenter, StyledButton } from "../components";
import { LangContext } from "../contexts/langContext";
import { TestStoreContext } from "../contexts/testStoreContext";
import { WebsocketContext } from "../contexts/websocketContext";
import useCurrentSlideData from "../hooks/useCurrentSlideData";
import useQuizSlideInfo from "../hooks/useQuizSlideInfo";
import { hasPlayedBefore } from "../util/webStorage";
import { answerStatsTypes } from "../interfaces/answerStats.interface";
import { SlideDataContext } from "@natklok/contexts/slideDataContext";
import { useNavigate } from "react-router-dom";
import useIsMinimizedQuiz from "@natklok/hooks/useIsMinimizedQuiz";

const ReadMoreOrContinue: React.FC<{
  readMoreInnerHTML: any;
  onlyReadMore?: boolean;
}> = ({ readMoreInnerHTML, onlyReadMore }) => {
  const { translatePageText, translateTranslationData } = useContext(LangContext);
  const [moreInfoExpanded, setMoreInfoExpanded] = useState(false);
  const [showFade, setShowFade] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShowFade(true);
    }, 1200);
  }, []);

  return (
    <div
      style={{
        opacity: showFade ? 1 : 0,
        transition: "all ease 0.5s",
        pointerEvents: showFade ? undefined : "none",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      {readMoreInnerHTML.sv !== "" && (
        <MoreInfoDisplay
          title={translatePageText("test.pressToReadMore")}
          content={translateTranslationData(readMoreInnerHTML)}
          setExpanded={setMoreInfoExpanded}
          buttonComponent={<NextQuestionOrDoTestButton />}
          onlyReadMore={onlyReadMore}
        />
      )}

      {moreInfoExpanded === false && !onlyReadMore && <NextQuestionOrDoTestButton />}
    </div>
  );
};

const NextQuestionOrDoTestButton: React.FC = () => {
  // Start a complete quiz
  // --- OR --- (based on if user has saved settings)
  // Go to next question
  const { translatePageText } = useContext(LangContext);
  const { questionsReducer } = useContext(SlideDataContext);
  const { inMultiplayerSession } = useContext(WebsocketContext);
  const currentSlideData = useCurrentSlideData();
  const { referredToQuiz, setReferredToQuiz } = useContext(TestStoreContext);
  const { amountSlidesUncompleted } = useQuizSlideInfo();
  const navigate = useNavigate();
  const minimized = useIsMinimizedQuiz();

  const referToTestTakerSettingsSlide = () => {
    navigate("/settings");
    setReferredToQuiz(undefined);
  };

  const referToNextQuestion = () => {
    // Mark current question as completed
    if (currentSlideData.question) {
      questionsReducer({
        type: "completeCurrentQuestion",
      });
    }

    if (amountSlidesUncompleted === 1) {
      if (minimized) {
        navigate("/minimized-results");
      } else {
        navigate("/results");
      }
    }
  };

  if (inMultiplayerSession) {
    return (
      <AlignCenter
        style={{
          width: "100%",
          textAlign: "center",
        }}
        withMaxWidth
      >
        {currentSlideData.answerStat && (
          <Box marginTop={2}>
            <AnswerStats
              useCurrentStat={false}
              useUnansweredAmount={false}
              config={{
                title: "",
                type: answerStatsTypes.horizontalBar,
                labelAndColorForBar1: {
                  label: "forTeachers.notAnswered",
                  mainColor: grey[300],
                  darkColor: grey[300],
                  brightColor: grey[300],
                },
                labelAndColorForBar2: {
                  label: "forTeachers.right",
                  mainColor: green[300],
                  darkColor: green[300],
                  brightColor: green[300],
                },
                labelAndColorForBar3: {
                  label: "forTeachers.wrong",
                  mainColor: red[300],
                  darkColor: red[300],
                  brightColor: red[300],
                },
              }}
              values={{
                bar1: currentSlideData.answerStat.amountNotAnswered,
                bar2: currentSlideData.answerStat.amountCorrect,
                bar3: currentSlideData.answerStat.amountIncorrect,
              }}
            />
          </Box>
        )}
        <Comments />
      </AlignCenter>
    );
  } else {
    if (referredToQuiz && amountSlidesUncompleted === 1 && !hasPlayedBefore()) {
      return (
        <div style={{ padding: "0 1em", textAlign: "center" }}>
          <Typography variant="h5" component="h3" style={{ marginBottom: 0, marginTop: 42 }}>
            {translatePageText("test.doTheTestTitle")}
          </Typography>
          <p style={{ marginTop: 6 }}>{translatePageText("test.doTheTestDesc")}</p>
          <StyledButton
            style={{ margin: "0px 0 18px 0", paddingLeft: 24, paddingRight: 24 }}
            onClick={() => referToTestTakerSettingsSlide()}
            label={translatePageText("welcome.test")}
          />
        </div>
      );
    } else if (currentSlideData?.question) {
      return (
        <div style={{ marginTop: 0, display: "flex", justifyContent: "center" }}>
          <div style={{ maxWidth: 520, width: "100%", padding: 12 }}>
            <StyledButton
              hasCooldown
              fullWidth
              icon={<HiArrowNarrowRight />}
              iconPlacement="right"
              style={{ marginTop: 18 }}
              onClick={referToNextQuestion}
              label={translatePageText(
                amountSlidesUncompleted === 1 ? "test.result" : "test.nextQuestion"
              )}
            />
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
              }}
            >
              <SeeQuestionAgainButton slideData={currentSlideData} />
              <SocialShare
                buttonText={translatePageText("general.shareTheQuestion")}
                textToShare={translatePageText("general.testOnNatklok")}
                path={window.location.pathname.slice(1)}
                linkTitle={currentSlideData.question.title.sv}
                linkMetaDesc={translatePageText("general.oneOfMany")}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }
};

export default ReadMoreOrContinue;
